import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Globe, Loader2 } from "lucide-react";
import api from "../lib/api";
import DealCard from "../components/DealCard";
import WebDealCard from "../components/WebDealCard";

export default function Search() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [webDeals, setWebDeals] = useState([]);
  const [webLoading, setWebLoading] = useState(false);
  const [webSource, setWebSource] = useState("");
  const [webError, setWebError] = useState("");
  const [merchantFilter, setMerchantFilter] = useState("all");

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    (async () => {
      const { data } = await api.get("/deals", { params: { search: q } });
      setDeals(data);
      setLoading(false);
    })();
  }, [q]);

  useEffect(() => {
    if (!q) return;
    setWebLoading(true);
    setWebError("");
    setWebDeals([]);
    setWebSource("");
    setMerchantFilter("all");
    (async () => {
      try {
        const { data } = await api.post("/search/web", { query: q, limit: 8 });
        setWebDeals(data.deals || []);
        setWebSource(data.source || "");
      } catch (err) {
        setWebError(err?.response?.data?.detail || "Web search unavailable");
      } finally {
        setWebLoading(false);
      }
    })();
  }, [q]);

  // Build merchant chip list from web results
  const merchants = useMemo(() => {
    const counts = new Map();
    webDeals.forEach((d) => {
      const key = (d.merchant || d.source_domain || "").trim();
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [webDeals]);

  const filteredWeb = useMemo(() => {
    if (merchantFilter === "all") return webDeals;
    return webDeals.filter((d) => (d.merchant || d.source_domain || "").trim() === merchantFilter);
  }, [webDeals, merchantFilter]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="search-page">
      <div className="mb-10">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3">Search results</div>
        <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter">
          &ldquo;<span className="italic font-normal">{q}</span>&rdquo;
        </h1>
        <p className="text-black/60 mt-3">{deals.length} curated {deals.length === 1 ? "match" : "matches"}</p>
      </div>

      {/* Curated section */}
      <section className="mb-14" data-testid="curated-section">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif font-black text-2xl tracking-tight">From DealHub</h2>
          <span className="text-[10px] font-mono uppercase tracking-widest text-black/40">Editor-picked</span>
        </div>
        {loading ? (
          <div className="text-black/40 font-mono uppercase tracking-widest text-xs" data-testid="curated-loading">Loading…</div>
        ) : deals.length === 0 ? (
          <div className="border border-dashed border-black/20 p-8 text-center">
            <div className="font-serif text-2xl">No curated deals match &ldquo;{q}&rdquo;</div>
            <p className="text-sm text-black/50 mt-2">Check the live web results below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {deals.map((d) => <DealCard key={d.id} deal={d} />)}
          </div>
        )}
      </section>

      {/* Live web section */}
      <section data-testid="web-section">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h2 className="font-serif font-black text-2xl tracking-tight flex items-center gap-2">
              <Globe className="w-5 h-5" strokeWidth={2} /> From around the web
            </h2>
            {webSource === "cache" && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-black/40">Cached</span>
            )}
            {webSource === "live" && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Live · Google Search</span>
            )}
          </div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-black/40">Save to bookmarks with the bookmark icon</span>
        </div>

        {/* Merchant filter chips */}
        {merchants.length > 1 && !webLoading && (
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="merchant-filter-chips">
            <button
              onClick={() => setMerchantFilter("all")}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border transition-colors ${merchantFilter === "all" ? "bg-black text-white border-black" : "bg-white text-black border-black/20 hover:border-black"}`}
              data-testid="merchant-chip-all"
            >
              All · {webDeals.length}
            </button>
            {merchants.map(([name, count]) => (
              <button
                key={name}
                onClick={() => setMerchantFilter(name)}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest border transition-colors ${merchantFilter === name ? "bg-black text-white border-black" : "bg-white text-black border-black/20 hover:border-black"}`}
                data-testid={`merchant-chip-${name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {name} · {count}
              </button>
            ))}
          </div>
        )}

        {webLoading ? (
          <div className="border border-dashed border-black/15 p-10 text-center" data-testid="web-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-black/40" strokeWidth={2} />
            <p className="text-xs font-mono uppercase tracking-widest text-black/40 mt-3">Crawling the internet for &ldquo;{q}&rdquo;…</p>
          </div>
        ) : webError ? (
          <div className="border border-dashed border-[#E63946]/30 p-6 text-center text-sm text-[#E63946]" data-testid="web-error">
            {webError}
          </div>
        ) : filteredWeb.length === 0 ? (
          <div className="border border-dashed border-black/15 p-8 text-center text-sm text-black/50" data-testid="web-empty">
            {webDeals.length === 0 ? "No live web results yet." : `No results for ${merchantFilter}.`}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="web-grid">
            {filteredWeb.map((d) => <WebDealCard key={d.id} deal={d} />)}
          </div>
        )}
      </section>
    </div>
  );
}
